/* eslint-disable max-classes-per-file */
/* eslint-disable @typescript-eslint/naming-convention */

import {
	ScheduledJob,
	ScheduledJobRequest,
	ScheduledJobStatus,
	ScheduledJobType
} from '@shure/cloud/shared/models/devices';
import { AnalyticsEvent } from '@shure/shared/models';

import { buildAnalyticsScheduledJob, mapScheduledJobTypeToAnalyticsType } from '../analytics.utils';

import { CdmAnalyticsEvents } from './cdm-analytics-events.enum';
import { AnalyticsCommonDeviceInfo, AnalyticsLocation } from './common.model';

export interface AnalyticsCommonScheduledJobInfo {
	job_id?: string;
	type: ScheduledJobType;
	scheduled_time: string;
	status?: ScheduledJobStatus;
	created_by?: string;
	created_at?: string;
	updated_by?: string;
	updated_at?: string;
	request_count: number;
	requests: AnalyticsScheduledJobRequest[];
	location?: ScheduledJobViewDetailsLocation | ScheduledJobEditLocation;
}

export interface AnalyticsScheduledJobRequest {
	device?: AnalyticsCommonDeviceInfo;
}

export interface AnalyticsFirmwareUpdateJobRequest extends AnalyticsScheduledJobRequest {
	current_firmware_version?: string;
	current_firmware_package_key?: string;
	update_to_firmware_version: string;
	update_to_firmware_package_key: string;
	hosted: boolean;
}

export type ScheduledJobEditLocation = AnalyticsLocation.JobsTableMenu | AnalyticsLocation.JobDetailsEditButton;

export type ScheduledJobViewDetailsLocation =
	| AnalyticsLocation.JobsTableMenu
	| AnalyticsLocation.JobsTableRowDoubleClick
	| AnalyticsLocation.PropertyPanel;

/**
 * Event to measure a user's behavior for scheduled job creation. Triggered when the user saves the created job.
 */
export class ScheduledJobCreatedEvent implements AnalyticsEvent {
	public readonly event = CdmAnalyticsEvents.ScheduledJobCreated;
	public readonly properties: {
		job: AnalyticsCommonScheduledJobInfo;
	};

	constructor(
		jobType: ScheduledJobType,
		executeAt: Date,
		requestCount: number,
		requests: AnalyticsScheduledJobRequest[]
	) {
		const analyticsJob: AnalyticsCommonScheduledJobInfo = {
			type: mapScheduledJobTypeToAnalyticsType(jobType),
			scheduled_time: executeAt.toISOString(),
			request_count: requestCount,
			requests: requests
		};
		this.properties = { job: analyticsJob };
	}
}

/**
 * Event when the user cancels a scheduled job.
 */
export class ScheduledJobCancelledEvent implements AnalyticsEvent {
	public readonly event = CdmAnalyticsEvents.ScheduledJobCancelled;
	public readonly properties: { job: AnalyticsCommonScheduledJobInfo };

	constructor(job: ScheduledJob, requests: ScheduledJobRequest[]) {
		this.properties = { job: buildAnalyticsScheduledJob(job, requests) };
	}
}

/**
 * Event when the user attempts to edit a scheduled job.
 */
export class ScheduledJobEditClickedEvent implements AnalyticsEvent {
	public readonly event = CdmAnalyticsEvents.ScheduledJobEditClicked;
	public readonly properties: { location: ScheduledJobEditLocation };

	constructor(location: ScheduledJobEditLocation) {
		this.properties = { location };
	}
}

/**
 * Event when the user discards edits on a scheduled job.
 */
export class ScheduledJobEditDiscardedEvent implements AnalyticsEvent {
	public readonly event = CdmAnalyticsEvents.ScheduledJobEditDiscarded;
	public readonly properties: { job: AnalyticsCommonScheduledJobInfo };

	constructor(job: ScheduledJob, requests: ScheduledJobRequest[]) {
		this.properties = { job: buildAnalyticsScheduledJob(job, requests) };
	}
}

/**
 * Event when the user updates a scheduled job. Triggered when the user saves their edits to an existing job.
 */
export class ScheduledJobEditSavedEvent implements AnalyticsEvent {
	public readonly event = CdmAnalyticsEvents.ScheduledJobEditSaved;
	public readonly properties: {
		job: AnalyticsCommonScheduledJobInfo;
	};

	constructor(job: ScheduledJob, requests: ScheduledJobRequest[]) {
		this.properties = { job: buildAnalyticsScheduledJob(job, requests) };
	}
}

/**
 * Event when the user views the details of a scheduled job. Triggered from job table menu, row double click, and property panel.
 */
export class ScheduledJobDetailsViewedEvent implements AnalyticsEvent {
	public readonly event = CdmAnalyticsEvents.ScheduledJobDetailsViewed;
	public readonly properties: { job: AnalyticsCommonScheduledJobInfo; location: ScheduledJobViewDetailsLocation };

	constructor(job: ScheduledJob, requests: ScheduledJobRequest[], location: ScheduledJobViewDetailsLocation) {
		this.properties = { job: buildAnalyticsScheduledJob(job, requests), location: location };
	}
}

/**
 * Event to measure how often a user manually refreshes the list of scheduled jobs.
 */
export class ScheduledJobListRefreshedEvent implements AnalyticsEvent {
	public readonly event = CdmAnalyticsEvents.ScheduledJobListRefreshed;
	public readonly properties = {};
}
